// 📄 pages/Admin/components/BadiFormModal.tsx
import React, { useState } from 'react';
import { X, Save } from 'lucide-react';
import {
  BadiObjeto,
  BadiObjetoData,
  SolucionBadi,
  TipoObjetoAbap,
  SOLUCIONES_BADI,
  TIPOS_OBJETO_ABAP,
  tipoObjetoMeta,
  saveBadi
} from '../../../services/catalogoBadisService.ts';

interface BadiFormModalProps {
  badi: BadiObjeto | null; // null = alta nueva
  onClose: () => void;
  onSaved: () => void;
}

const generarId = (): string => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const EMPTY_FORM: BadiObjetoData = {
  nombre: '',
  solucion: 'general',
  tipo: 'badi',
  transaccion: 'SE18',
  implementacionZ: '',
  claseImplementacion: '',
  metodoInterfaz: '',
  programaInclude: '',
  descripcion: ''
};

export default function BadiFormModal({ badi, onClose, onSaved }: BadiFormModalProps) {
  const [form, setForm] = useState<BadiObjetoData>(
    badi
      ? {
          nombre: badi.nombre,
          solucion: badi.solucion,
          tipo: badi.tipo,
          transaccion: badi.transaccion,
          implementacionZ: badi.implementacionZ,
          claseImplementacion: badi.claseImplementacion,
          metodoInterfaz: badi.metodoInterfaz,
          programaInclude: badi.programaInclude,
          descripcion: badi.descripcion
        }
      : EMPTY_FORM
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: keyof BadiObjetoData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Al cambiar el Tipo de Objeto, sugiere la transacción típica —
  // solo si el usuario no escribió ya una propia, para no pisarla.
  const handleTipoChange = (tipo: TipoObjetoAbap) => {
    setForm((prev) => ({
      ...prev,
      tipo,
      transaccion: prev.transaccion.trim() ? prev.transaccion : tipoObjetoMeta(tipo).transaccionSugerida
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.nombre.trim()) {
      setError('El nombre del objeto/BAdI es obligatorio.');
      return;
    }

    setSaving(true);
    try {
      await saveBadi(badi?.id || generarId(), form, !badi);
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Error al guardar el registro.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-lg font-medium text-white">
            {badi ? 'Editar Objeto ABAP' : 'Nuevo Objeto ABAP'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
          )}

          <div>
            <label className={labelClass}>Nombre del Objeto / BAdI <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={form.nombre}
              onChange={(e) => handleChange('nombre', e.target.value)}
              placeholder="ej. ARBCIG_PURREQ - BAdI de PR de Bajada"
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Solución / Canal</label>
              <select
                className={inputClass}
                value={form.solucion}
                onChange={(e) => handleChange('solucion', e.target.value as SolucionBadi)}
              >
                {SOLUCIONES_BADI.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Tipo de Objeto</label>
              <select
                className={inputClass}
                value={form.tipo}
                onChange={(e) => handleTipoChange(e.target.value as TipoObjetoAbap)}
              >
                {TIPOS_OBJETO_ABAP.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={labelClass}>Transacción SAP</label>
            <input
              type="text"
              value={form.transaccion}
              onChange={(e) => handleChange('transaccion', e.target.value)}
              placeholder={`Sugerida para este tipo: ${tipoObjetoMeta(form.tipo).transaccionSugerida}`}
              className={`${inputClass} font-mono`}
            />
            <p className="text-[10px] text-gray-500 mt-1">
              Se sugiere automáticamente según el Tipo de Objeto — puedes editarla libremente (ej. "SE18 / SE38" si aplica más de una).
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>BAdI / Implementación Z (Cliente)</label>
              <input
                type="text"
                value={form.implementacionZ}
                onChange={(e) => handleChange('implementacionZ', e.target.value)}
                placeholder="ej. ZBADI_ARBCIG_PURREQ"
                className={`${inputClass} font-mono`}
              />
            </div>
            <div>
              <label className={labelClass}>Clase de Implementación</label>
              <input
                type="text"
                value={form.claseImplementacion}
                onChange={(e) => handleChange('claseImplementacion', e.target.value)}
                placeholder="ej. ZCLMEXMM0_ARBCIG_PURREQ"
                className={`${inputClass} font-mono`}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Método / Interface</label>
            <input
              type="text"
              value={form.metodoInterfaz}
              onChange={(e) => handleChange('metodoInterfaz', e.target.value)}
              placeholder="ej. IF_ARBCIG_PURREQ~PREPROCESSING"
              className={`${inputClass} font-mono`}
            />
          </div>

          <div>
            <label className={labelClass}>Programa / Include / Interfaz Receptora</label>
            <input
              type="text"
              value={form.programaInclude}
              onChange={(e) => handleChange('programaInclude', e.target.value)}
              placeholder="ej. LMLSRF0F o DespatchedDeliveryNotification_Async_In"
              className={`${inputClass} font-mono`}
            />
          </div>

          <div>
            <label className={labelClass}>Descripción / Propósito</label>
            <textarea
              value={form.descripcion}
              onChange={(e) => handleChange('descripcion', e.target.value)}
              placeholder="Para qué sirve este objeto dentro de la integración..."
              rows={3}
              className={`${inputClass} resize-none`}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-[#fff] transition-all"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
